import { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

function PlanComparison({ currentPlan = 'free', onSelectPlan }) {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get('/plans');
      setPlans(response.data?.plans || []);
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    if (onSelectPlan) {
      onSelectPlan(plan, billingCycle);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <span className="spinner w-8 h-8"></span>
      </div>
    );
  }

  return (
    <div>
      {/* Billing cycle toggle */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-800 rounded-lg p-1 inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-md transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-primary-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-4 py-2 rounded-md transition-colors ${
              billingCycle === 'yearly'
                ? 'bg-primary-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Yearly <span className="text-green-400 text-sm">Save 20%</span>
          </button>
        </div>
      </div>

      {/* Plans grid */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        {plans.map((plan) => {
          const isCurrent = plan.slug === currentPlan;
          const price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly;
          const monthlyCharacters =
            plan.monthly_limit && plan.max_characters
              ? plan.monthly_limit * plan.max_characters
              : null;
          
          return (
            <div
              key={plan.id}
              className={`card relative ${
                plan.is_popular ? 'ring-2 ring-primary-500' : ''
              } ${isCurrent ? 'bg-gray-750' : ''}`}
            >
              {plan.is_popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold">
                    ${billingCycle === 'monthly' ? price : (price / 12).toFixed(0)}
                  </span>
                  <span className="text-gray-400">/month</span>
                </div>
                {billingCycle === 'yearly' && price > 0 && (
                  <p className="text-sm text-gray-400">
                    ${price} billed annually
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {/* Character limit */}
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>
                    {monthlyCharacters
                      ? `${(monthlyCharacters / 1000).toFixed(0)}k characters/month`
                      : 'Unlimited characters'}
                  </span>
                </li>

                {/* Voice cloning */}
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>
                    {plan.voice_cloning_enabled
                      ? `${plan.max_cloned_voices || 'Unlimited'} voice clones`
                      : 'No voice cloning'}
                  </span>
                </li>

                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{plan.workspace_seats || 1} workspace seats</span>
                </li>

                {/* Premium voices */}
                <li className="flex items-center gap-2">
                  {plan.premium_voices_enabled ? (
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className={!plan.premium_voices_enabled ? 'text-gray-500' : ''}>
                    Premium voices
                  </span>
                </li>

                {/* API access */}
                <li className="flex items-center gap-2">
                  {plan.api_access_enabled ? (
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className={!plan.api_access_enabled ? 'text-gray-500' : ''}>
                    API access
                  </span>
                </li>

                {/* Additional features */}
                {plan.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan)}
                disabled={isCurrent}
                className={`w-full btn ${
                  isCurrent
                    ? 'bg-gray-600 cursor-not-allowed'
                    : plan.is_popular
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                {isCurrent ? 'Current Plan' : price === 0 ? 'Get Started' : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PlanComparison;
